import {
  canEditDriverInTimeFor,
  canEditDriverOutTimeFor,
  collectStopChronologyViolationsFor,
  getActiveIndices,
  isWaterfallActiveForMode,
  positionForStopIdIn,
  validateStopTimeChangeAtPositionFor,
  type DriverTimeField,
  type StopChronologyViolation,
  type StopTimeValidationResult,
} from '@common/order/stopChronology';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  EdiUpdate,
  featureFlagGetCustomerFeatures,
  orderStore,
  setOrderStore,
} from '@store/orders';
import { stops } from '@store/orders/selectors';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { produce } from 'solid-js/store';

// Re-export the pure (store-independent) chronology API so existing imports
// from this module keep working. The real implementations live in
// `@common/order/stopChronology` to break the
// `store/orders -> utils/validationUtils -> views -> store/orders` cycle.
export {
  canEditDriverInTimeFor,
  canEditDriverOutTimeFor,
  findStopChronologyViolationFor,
  isWaterfallActiveForMode,
  validateStopTimeChangeAtPositionFor,
  validateStopTimeChangeFor,
} from '@common/order/stopChronology';
export type {
  DriverTimeField,
  StopChronologyViolation,
  StopTimeValidationResult,
} from '@common/order/stopChronology';

const activeLoad = () => orderStore.order.loads[orderStore.activeTab.index];
const activeLoadStops = (): LastUsedTopStopModel[] => activeLoad().stops ?? [];

/** True when every active stop before `position` has both In and Out times. */
const priorsCompleteForActive = (
  stops: LastUsedTopStopModel[],
  position: number,
): boolean => {
  const activeIndices = getActiveIndices(stops);
  for (let i = 0; i < position; i++) {
    const prev = stops[activeIndices[i]];
    if (!prev.driverInTime || !prev.driverOutTime) return false;
  }
  return true;
};

export const isStopWaterfallActive = (): boolean =>
  isWaterfallActiveForMode(activeLoad().mode);

const activeLoadHasCarrier = (): boolean => Boolean(activeLoad().carrierId);

export const canEditDriverInTime = (stopIndex: number): boolean =>
  canEditDriverInTimeFor(activeLoadStops(), stopIndex, activeLoad().mode);

export const canEditDriverOutTime = (stopIndex: number): boolean =>
  canEditDriverOutTimeFor(activeLoadStops(), stopIndex, activeLoad().mode);

/**
 * Editor (Stop modal) variant of `canEditDriverInTime` that locates the stop
 * by id (or treats `null`/missing as a new stop appended to the end).
 */
export const canEditDriverInTimeForEditor = (
  stopId: number | null | undefined,
  mode: string | null | undefined,
): boolean => {
  if (
    isFeatureFlagEnabled(
      FeatureFlags.RequireCarrierAssignmentToProgressStatus,
    )() &&
    !activeLoadHasCarrier() &&
    mode !== 'LTL'
  )
    return false;
  if (!isStopWaterfallActive()) return true;
  const stops = activeLoadStops();
  const activeIndices = getActiveIndices(stops);
  const position = positionForStopIdIn(stops, activeIndices, stopId);
  if (position <= 0) return true;
  return priorsCompleteForActive(stops, position);
};

/**
 * Editor variant of `canEditDriverOutTime`. Driver Out unlocks when the
 * form's Driver In date and time are both entered AND every prior active
 * stop has a complete In/Out chain.
 */
export const canEditDriverOutTimeForEditor = (
  stopId: number | null | undefined,
  driverDateIn: string | null | undefined,
  driverTimeIn: string | null | undefined,
  mode: string | null | undefined,
): boolean => {
  if (
    isFeatureFlagEnabled(
      FeatureFlags.RequireCarrierAssignmentToProgressStatus,
    )() &&
    !activeLoadHasCarrier() &&
    mode !== 'LTL'
  )
    return false;
  if (!isStopWaterfallActive()) return true;
  if (!driverDateIn || !driverTimeIn) return false;
  const stops = activeLoadStops();
  const activeIndices = getActiveIndices(stops);
  const position = positionForStopIdIn(stops, activeIndices, stopId);
  if (position <= 0) return true;
  return priorsCompleteForActive(stops, position);
};

/**
 * Returns the index of `stopId` within the active (non-deleted) stop
 * ordering. For `null`/`undefined`/missing ids returns the position the stop
 * would occupy if appended to the end (used for new-stop creation).
 */
export const positionForStopId = (
  stopId: number | null | undefined,
): number => {
  const stops = activeLoadStops();
  return positionForStopIdIn(stops, getActiveIndices(stops), stopId);
};

/**
 * Validates a candidate Driver In / Driver Out timestamp against intra-stop
 * and inter-stop chronology rules. All comparisons are performed in UTC.
 *
 * @param position           Index of the stop within the active-stop ordering.
 *                           May equal `activeIndices.length` for an appended
 *                           new stop (editor modal).
 * @param field              Which field is being changed.
 * @param newIso             Proposed ISO timestamp (or null to clear).
 * @param ownPartnerIso      The same stop's *partner* timestamp:
 *                           - For `driverInTime` -> the stop's `driverOutTime`.
 *                           - For `driverOutTime` -> the stop's `driverInTime`.
 *                           Pass the most up-to-date value (form state in the
 *                           modal, store value on the summary table).
 */
export const validateStopTimeChange = (
  position: number,
  field: DriverTimeField,
  newIso: string | null,
  ownPartnerIso: string | null | undefined,
): StopTimeValidationResult =>
  validateStopTimeChangeAtPositionFor(
    activeLoadStops(),
    activeLoad().mode,
    position,
    field,
    newIso,
    ownPartnerIso,
  );

/**
 * Save-time guard: walks every load and returns every stop chronology
 * violation across the whole order. Thin adapter around the pure
 * `collectStopChronologyViolationsFor` that supplies the current order's
 * loads from `orderStore`.
 */
export const collectStopChronologyViolations = (): StopChronologyViolation[] =>
  collectStopChronologyViolationsFor(orderStore.order.loads);

type StopType = 'pickUp' | 'dropOff';
type DriverAction = 'in' | 'out';

const AUTO214_FIELD_MAP = {
  pickUp: {
    in: 'shouldSendPickupDriverInAuto214',
    out: 'shouldSendPickupDriverOutAuto214',
  },
  dropOff: {
    in: 'shouldSendDropoffDriverInAuto214',
    out: 'shouldSendDropoffDriverOutAuto214',
  },
} as const;

export const dirtyTracking = (
  stopType: StopType,
  driverAction: DriverAction,
  index: number,
) => {
  const field = AUTO214_FIELD_MAP[stopType][driverAction];
  setOrderStore(
    'order',
    'loads',
    orderStore.activeTab.index,
    'stops',
    index,
    produce((stop) => {
      stop.shouldSendEdiBooleanVals[field] = true;
    }),
  );
};

export const createShowAuto214 = (stopIndex: number) => {
  const stop = stops()[stopIndex];
  let ediUpdates = stop.ediUpdates ? [...stop.ediUpdates] : [];
  if (stop.pickUp) {
    ediUpdates = ediUpdates.filter(
      (update) =>
        !['X1', 'CD', 'AB'].includes(update.shipmentStatus ?? '') &&
        update.appointmentStatus !== 'AB',
    );
  } else if (stop.dropOff) {
    ediUpdates = ediUpdates.filter(
      (update) =>
        !['X3', 'AF', 'AA'].includes(update.shipmentStatus ?? '') &&
        update.appointmentStatus !== 'AA',
    );
  }
  //remove the edi updates that are not relevant to the current stop
  const findIndexByKey = (key: keyof EdiUpdate, value: string) => {
    return ediUpdates.findIndex((item) => item[key] === value);
  };
  const addOrUpdateEdiUpdate = (
    key: keyof EdiUpdate,
    value: string,
    update: EdiUpdate,
  ) => {
    const index = findIndexByKey(key, value);
    if (index === -1) {
      ediUpdates.push(update);
    } else {
      ediUpdates[index] = { ...ediUpdates[index], ...update };
    }
  };
  //Note: THese are required for any EDI Update
  const defaultEdiProperties = {
    latitude: null,
    longitude: null,
    note: null,
    auto214: true,
    statusReason: 'NS',
    temperature: null,
    stopId: stop.id,
    loadId: stop.loadId,
    state: stop.state ?? '',
    city: stop.city ?? '',
  };
  if (stop.pickUp) {
    if (
      stop.shouldSendEdiBooleanVals.shouldSendPickupDriverInAuto214 &&
      featureFlagGetCustomerFeatures.featureFlagCustomer
        .EDIPickupDriverInAuto214
    ) {
      addOrUpdateEdiUpdate('shipmentStatus', 'X3', {
        shipmentStatus: 'X3',
        appointmentStatus: null,
        date: stop.driverInTime as string,
        appointmentNumber: null,
        timeZone: 'LT',
        ...defaultEdiProperties,
      });
    }
    if (
      stop.shouldSendEdiBooleanVals.shouldSendPickupDriverOutAuto214 &&
      featureFlagGetCustomerFeatures.featureFlagCustomer
        .EDIPickupDriverOutAuto214
    ) {
      addOrUpdateEdiUpdate('shipmentStatus', 'AF', {
        shipmentStatus: 'AF',
        appointmentStatus: null,
        date: stop.driverOutTime as string,
        appointmentNumber: null,
        timeZone: 'LT',
        ...defaultEdiProperties,
      });
    }
  }
  if (stop.dropOff) {
    if (
      stop.shouldSendEdiBooleanVals.shouldSendDropoffDriverInAuto214 &&
      featureFlagGetCustomerFeatures.featureFlagCustomer
        .EDIDropoffDriverInAuto214
    ) {
      addOrUpdateEdiUpdate('shipmentStatus', 'X1', {
        shipmentStatus: 'X1',
        appointmentStatus: null,
        date: stop.driverInTime as string,
        appointmentNumber: null,
        timeZone: 'LT',
        ...defaultEdiProperties,
      });
    }
    if (
      stop.shouldSendEdiBooleanVals.shouldSendDropoffDriverOutAuto214 &&
      featureFlagGetCustomerFeatures.featureFlagCustomer
        .EDIDropoffDriverOutAuto214
    ) {
      addOrUpdateEdiUpdate('shipmentStatus', 'CD', {
        shipmentStatus: 'CD',
        appointmentStatus: null,
        date: stop.driverOutTime as string,
        appointmentNumber: null,
        timeZone: 'LT',
        ...defaultEdiProperties,
      });
    }
  }
  return ediUpdates;
};
